import { useLocation, useNavigate } from 'react-router-dom'
import { Home, Search, HeartHandshake, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BOTTOM_NAV_TABS } from '@/lib/constants'
import { useUnreadCount } from '@/features/notification/api'

/** 아이콘 매핑 */
const iconMap = {
  Home,
  Search,
  HeartHandshake,
  User,
} as const

/**
 * 하단 탭 네비게이션 컴포넌트 v2.0
 *
 * - 활성 탭: 필 배경 + 컬러 아이콘
 * - 비활성: 그레이 아이콘
 * - 상단 보더 대신 그림자
 * - 최소 터치 영역 48dp 준수
 * - Safe Area 고려 (bottom padding)
 */
export default function BottomNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const { data: unreadCount } = useUnreadCount()

  // 현재 경로에 가장 구체적으로(가장 긴 prefix) 매칭되는 탭 1개만 활성화.
  // 돌봄일지(/mypage/care-log)가 마이(/mypage)의 하위 경로라, 단순 startsWith로는
  // 두 탭이 동시에 켜지는 버그가 있어 최장 prefix 매칭으로 하나만 선택한다.
  const activeTabPath = BOTTOM_NAV_TABS.reduce<string | null>((best, tab) => {
    const matched =
      tab.path === '/'
        ? location.pathname === '/'
        : location.pathname === tab.path || location.pathname.startsWith(tab.path + '/')
    if (!matched) return best
    return best == null || tab.path.length > best.length ? tab.path : best
  }, null)

  return (
    <nav
      className="sticky bottom-0 z-40 bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgb(0_0_0/0.06)]"
      aria-label="주요 메뉴"
    >
      <div className="flex h-16 items-center justify-around px-2">
        {BOTTOM_NAV_TABS.map((tab) => {
          const Icon = iconMap[tab.icon as keyof typeof iconMap]
          const isActive = tab.path === activeTabPath

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={cn(
                'flex h-12 min-w-[4rem] flex-col items-center justify-center gap-0.5 rounded-2xl transition-all duration-200',
                isActive
                  ? 'bg-cb-primary-light text-cb-primary'
                  : 'text-cb-gray-400 hover:text-cb-gray-600 hover:bg-cb-gray-50'
              )}
              aria-label={tab.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="relative">
                <Icon size={22} strokeWidth={isActive ? 2.5 : 1.8} />
                {tab.path === '/mypage' && unreadCount != null && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1.5 h-2.5 w-2.5 rounded-full bg-red-500" />
                )}
              </div>
              <span className={cn(
                'text-[10px] leading-tight',
                isActive ? 'font-bold' : 'font-medium'
              )}>
                {tab.label}
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
